import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Typography,
  Tooltip,
  IconButton,
  Container,
  Paper,
  Divider
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Info as InfoIcon, Lock as LockIcon } from '@material-ui/icons';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import axiosAuth from '../../../utils/axiosWithAuth';
import invoiageLogo from "../../../images/branding/invoiage/invoiage-transparent-logo-small.png";

// Define custom styles using makeStyles
const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%)', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  logo: {
    display: 'block',
    margin: '0 auto 20px',
    maxWidth: '200px',
    height: 'auto',
  },
  container: {
    [theme.breakpoints.up('sm')]: {
      marginBottom: theme.spacing(8),
    },
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    backgroundColor: '#ffffff',
    maxWidth: 600,
    margin: '0 auto',
  },
  header: {
    '&.MuiTypography-root': {
      marginTop: theme.spacing(1),
      color: '#1a237e', // Deep blue for trust
      marginBottom: theme.spacing(1),
    }
  },
  subheader: {
    '&.MuiTypography-root': {
      color: '#455a64', // Softer gray for secondary text
      lineHeight: 1.5,
    }
  },
  divider: {
    '&.MuiDivider-root': {
      margin: theme.spacing(2, 0),
      backgroundColor: '#e0e0e0',
      border: 'none', // Remove default border
      height: 1, // Ensure divider is visible
    },
  },
  button: {
    '&.MuiButton-root': {
      padding: theme.spacing(1.5),
      fontWeight: 500,
      textTransform: 'none', // Less aggressive button text
      backgroundColor: '#001357', // Brand blue
      '&:hover': {
        backgroundColor: '#001045', // Darker blue on hover
      },
    }
  },
  lockIcon: {
    color: '#757575', // Neutral gray for the lock icon
  },
}));

const CredentialForm = () => {
  const classes = useStyles();
  const { uuid } = useParams();

  // State for form fields and submission status
  const [onlineAccessUrl, setOnlineAccessUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [twoFaInstructions, setTwoFaInstructions] = useState('');
  const [otherDetails, setOtherDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [valid, setValid] = useState(false);

  const resetForm = () => {
    setOnlineAccessUrl('');
    setUsername('');
    setPassword('');
    setTwoFaInstructions('');
    setOtherDetails('');
    setLoading(false);
    setError(null);
    setSuccess(false);
  };

  const urlIsValid = (onlineAccessUrl) =>
    /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(:[\d]{1,5})?(\/[\w\/.-]*(\?[\w=&%+.-]*)?(#[\w\/.-]*)?)?$/i.test(onlineAccessUrl);

  useEffect(() => {
    setValid(username && password && onlineAccessUrl && urlIsValid(onlineAccessUrl));
  }, [onlineAccessUrl, password, username]);

  const validateUrl = () => {
    if (onlineAccessUrl && !urlIsValid(onlineAccessUrl)) {
      setError('Please enter a valid Website URL');
    } else {
      setError('');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Basic validation for required fields
    if (!onlineAccessUrl || !username || !password) {
      setError('Missing required information.');
      setLoading(false);
      return;
    }

    // Prepare JSON object for submission
    const formData = {
      personalizedCode: uuid,
      online_access_url: onlineAccessUrl,
      online_access_username: username,
      online_access_password: password,
      twoFaInstructions,
      otherDetails,
    };

    try {
      await axiosAuth().post('/credentials-transfer', formData);
      setSuccess(true);
    } catch (err) {
      setError('An error occurred while submitting your credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Container maxWidth="sm" className={classes.container}>
        <Paper className={classes.paper}>
          {/* Company Logo */}
          <img src={invoiageLogo} alt="Invoiage Logo" className={classes.logo} />
          <Divider className={classes.divider} />
          <form onSubmit={handleSubmit} autoComplete="off">
            <Grid container spacing={3}>
              {/* Form Header */}
              <Grid item xs={12}>
                <Typography variant="h5" align="center" className={classes.header}>
                  Utility Account Credential Submission
                </Typography>
                <Typography variant="body2" align="center" className={classes.subheader}>
                  Help us retrieve your utility bills by providing your account credentials below.
                  Your information is encrypted and stored securely.
                </Typography>
              </Grid>

              {/* Online Access URL */}
              <Grid item xs={12}>
                <TextField
                  label="Utility Website URL"
                  value={onlineAccessUrl}
                  onChange={(e) => setOnlineAccessUrl(e.target.value)}
                  onBlur={validateUrl}
                  fullWidth
                  autoComplete="nope"
                  name="online_access_123_url"
                  required
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="The website address where you log in to your utility account.">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* Username */}
              <Grid item xs={12}>
                <TextField
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  fullWidth
                  required
                  autoComplete="off"
                  name="userfadsfsdaname_123"
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="The username or email you use to log in to your utility account.">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* Password */}
              <Grid item xs={12}>
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  autoComplete="nope"
                  name="password_123"
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Your utility account password. It’s masked and encrypted for your security.">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* 2FA Instructions */}
              <Grid item xs={12}>
                <TextField
                  label="Two-Factor Authentication (2FA) Instructions"
                  value={twoFaInstructions}
                  onChange={(e) => setTwoFaInstructions(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Provide any 2FA details, like security question answers or app instructions.">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* Other Details */}
              <Grid item xs={12}>
                <TextField
                  label="Additional Notes"
                  value={otherDetails}
                  onChange={(e) => setOtherDetails(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Tell us which accounts should be tracked, how far back should history be fetched (date), and any extra info to help us access your account successfully.">
                        <IconButton tabIndex={-1}>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!valid || loading || success}
                  fullWidth
                  className={classes.button}
                >
                  {loading ? 'Submitting...' : success ? 'Submitted' : 'Submit Credentials'}
                </Button>
              </Grid>

              {/* Feedback Messages */}
              {error && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {error}
                  </Typography>
                </Grid>
              )}
              {success && (
                <Grid item xs={12}>
                  <Typography
                    color="primary"
                    variant="body2"
                    style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}
                  >
                    Success! Your credentials have been submitted.
                    <Button variant="contained" onClick={resetForm}>
                      Reset Form <RefreshIcon />
                    </Button>
                  </Typography>
                </Grid>
              )}
            </Grid>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Tooltip title="Learn how Invoiage keeps your data secure">
                <a
                  href="https://www.invoiage.ai/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classes.lockIcon}
                >
                  <IconButton>
                    <LockIcon color="primary" />
                  </IconButton>
                </a>
              </Tooltip>
            </div>
          </form>
        </Paper>
      </Container>
    </div>
  );
};

export default CredentialForm;